import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AgentInfo, AgentType, AgentCategory } from '../../types';
import { useAgents } from '../../hooks/useAgents';
import { AGENTS as FALLBACK_AGENTS } from '../../constants/agents';
import { 
  ChevronDownIcon, 
  ArrowLeftStartOnRectangleIcon,
  MagnifyingGlassIcon,
  SquaresPlusIcon,
  LightBulbIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG, API_ENDPOINTS } from '../../config/api';
import { getBackendDirectOrigin } from '../../config/environment';
import { getAccessToken } from '../../utils/authStorage';

interface SidebarProps {
  selectedAgentId: AgentType | null;
  onSelectAgent: (agentId: AgentType) => void;
  isOpen: boolean;
  toggleSidebar: () => void; 
}

const Sidebar: React.FC<SidebarProps> = ({ selectedAgentId, onSelectAgent, isOpen, toggleSidebar }) => {
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  const [flyoutCategory, setFlyoutCategory] = useState<AgentCategory | null>(null);
  const [mounted, setMounted] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const { theme, systemTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch unread notifications count for badge
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const token = getAccessToken();
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_NOTIFICATIONS_UNREAD_COUNT}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadNotificationsCount(data.count ?? 0);
      }
    } catch {
      setUnreadNotificationsCount(0);
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Refetch when visiting support-messages page (to update badge after marking as read)
  useEffect(() => {
    if (location.pathname === '/app/support-messages') {
      fetchUnreadCount();
    }
  }, [location.pathname, fetchUnreadCount]);

  // Listen for badge refresh when user views/responds to messages
  useEffect(() => {
    const handler = () => fetchUnreadCount();
    window.addEventListener('support-notifications-updated', handler);
    return () => window.removeEventListener('support-notifications-updated', handler);
  }, [fetchUnreadCount]);

  const isLightMode = mounted && (theme === 'system' ? systemTheme !== 'dark' : theme === 'light');
  const sidebarLogoSrc = isOpen
    ? (isLightMode ? '/imgs/logos/EscriturIA-logo-horizontal-sem-slogan.png' : '/imgs/logos/EscriturIA-logo-horizontal.png')
    : '/imgs/favicon/favicon-32x32.png';
  
  // Buscar agentes da API (filtrados automaticamente por status='active')
  const { agents: apiAgents, loading: agentsLoading, error: agentsError } = useAgents();
  
  // Usar agentes da API se disponíveis e não estiver carregando, senão usar fallback
  // Verificar se apiAgents tem mais que apenas o agente WELCOME (que é especial)
  const hasApiAgents = apiAgents && apiAgents.length > 1 && !agentsLoading;
  const AGENTS = hasApiAgents ? apiAgents : FALLBACK_AGENTS;
  
  // Close flyout when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target as Node)) {
        setFlyoutCategory(null);
      }
    };
    
    if (flyoutCategory) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [flyoutCategory]);

  const handleCategoryToggle = (category: AgentCategory) => {
    if (!isOpen) {
      // When collapsed, toggle flyout
      setFlyoutCategory(flyoutCategory === category ? null : category);
    } else {
      // When open, toggle accordion
      setOpenCategories(prev => ({
        ...prev,
        [category]: !prev[category],
      }));
    }
  };
  
  const topLevelAgents = (AGENTS || []).filter(a => !a.category);
  
  const categoryOrder: AgentCategory[] = [
    AgentCategory.INVESTIGATING_TEXTS,
    AgentCategory.MAPPING_THEMES,
    AgentCategory.EXPLORING_MEANINGS,
    AgentCategory.PREPARING_MESSAGES,
  ];
  
  // Icons for categories
  const categoryIcons: Record<AgentCategory, React.ReactNode> = {
    [AgentCategory.INVESTIGATING_TEXTS]: <MagnifyingGlassIcon className="w-6 h-6" />,
    [AgentCategory.MAPPING_THEMES]: <SquaresPlusIcon className="w-6 h-6" />,
    [AgentCategory.EXPLORING_MEANINGS]: <LightBulbIcon className="w-6 h-6" />,
    [AgentCategory.PREPARING_MESSAGES]: <MegaphoneIcon className="w-6 h-6" />,
  };

  const agentsByCategory = (AGENTS || []).reduce((acc, agent) => {
    if (agent.category) {
      if (!acc[agent.category]) {
        acc[agent.category] = [];
      }
      acc[agent.category].push(agent);
    }
    return acc;
  }, {} as Record<AgentCategory, AgentInfo[]>);

  // Sort agents within each category alphabetically
  for (const category in agentsByCategory) {
      agentsByCategory[category as AgentCategory].sort((a, b) => a.name.localeCompare(b.name));
  }

  const renderAgentButton = (agent: AgentInfo, inFlyout: boolean = false) => {
    const isDevocional = agent.id === AgentType.DEVOCIONAL_PESSOAL;
    // Ensure only one agent is selected at a time - strict comparison
    const isSelected = selectedAgentId !== null && selectedAgentId === agent.id;
    const textColor = isSelected 
      ? 'text-brand-sidebar-active-text' 
      : 'text-brand-sidebar-text group-hover:text-brand-accent';
    
    // Para Devocional Pessoal, borda na cor do texto
    // Cores: normal = #c4b99a, selected = #180801, hover = #bf9026
    const getBorderStyle = () => {
      if (!isDevocional) return {};
      if (isSelected) {
        return { borderColor: '#180801', borderWidth: '1px', borderStyle: 'solid' };
      }
      return { borderColor: '#c4b99a', borderWidth: '1px', borderStyle: 'solid' };
    };
    
    // Todos os itens usam o mesmo espaçamento padrão (mb-2 = 8px = 0.5rem)
    const marginClass = "mb-2";
    
    // Para itens no flyout, também usar mb-2
    const flyoutMarginClass = inFlyout ? "mb-2" : "";
    
    // IMPORTANTE: Ícones só aparecem quando menu está completamente fechado (sem flyout)
    // Quando menu está aberto (isOpen = true) OU quando está no flyout (inFlyout = true), NÃO mostrar ícones
    // Os ícones só devem aparecer na barra lateral colapsada, não no flyout nem no menu expandido
    const shouldShowIcon = !isOpen && !inFlyout;
    
    return (
    <li 
      key={agent.id} 
      className={`px-2 ${inFlyout ? flyoutMarginClass : marginClass}`}
      style={{ marginBottom: '0.5rem' }} // mb-2 = 8px = 0.5rem - estilo inline tem prioridade
    >
      <button
        onClick={() => {
          onSelectAgent(agent.id);
          setFlyoutCategory(null); // Close flyout after selection
        }}
        className={`w-full flex items-center p-2 rounded-md transition-colors duration-150 group
          ${isSelected 
            ? 'bg-brand-sidebar-active-bg text-brand-sidebar-active-text shadow-md' 
            : 'hover:bg-brand-sidebar-hover-bg hover:text-brand-accent'
          }
          ${!inFlyout && isOpen ? 'justify-start' : !inFlyout ? 'md:justify-center' : 'justify-start'}
          ${isDevocional ? 'border' : ''}
        `}
        style={isDevocional ? {
          ...getBorderStyle(),
          ...(isSelected ? {} : {
            '--hover-border-color': '#bf9026'
          } as React.CSSProperties)
        } : undefined}
        onMouseEnter={(e) => {
          if (isDevocional && !isSelected) {
            e.currentTarget.style.borderColor = '#bf9026';
          }
        }}
        onMouseLeave={(e) => {
          if (isDevocional && !isSelected) {
            e.currentTarget.style.borderColor = '#c4b99a';
          }
        }}
        aria-current={selectedAgentId === agent.id ? "page" : undefined}
      >
        {/* Ícones só aparecem quando menu está fechado OU quando está no flyout */}
        {shouldShowIcon && (
          <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
            {agent.icon && React.isValidElement(agent.icon) 
              ? React.cloneElement(agent.icon as React.ReactElement<{ className?: string }>, { 
                  className: `w-6 h-6 ${textColor}`.trim()
                })
              : <div className="w-6 h-6" aria-hidden="true"></div>}
          </div>
        )}
        <span className={`${shouldShowIcon ? 'ml-3' : ''} text-sm font-medium whitespace-nowrap ${textColor} ${!inFlyout && isOpen ? 'inline' : inFlyout ? 'inline' : 'md:hidden'}`}>{agent.name}</span>
      </button>
    </li>
    );
  };

  const renderCategory = (category: AgentCategory, agents: AgentInfo[]) => {
      const isCategoryOpen = !!openCategories[category];
      const categoryIcon = categoryIcons[category];
      
      return (
        <React.Fragment key={category}>
            {isOpen ? (
              // Sidebar open: Show accordion header
              <>
                <li className="px-2 mt-5">
                  <button
                    onClick={() => handleCategoryToggle(category)}
                    className="w-full flex items-center justify-between p-2 rounded-md transition-colors duration-150 group text-brand-text-secondary bg-white/5 hover:bg-white/10"
                    aria-expanded={isCategoryOpen}
                  >
                    <span className="text-xs font-semibold tracking-wide uppercase">
                      {category}
                    </span>
                    <ChevronDownIcon
                      className={`w-4 h-4 text-brand-text-secondary transition-transform duration-200 ${!isCategoryOpen ? '-rotate-90' : ''}`}
                    />
                  </button>
                </li>
                {isCategoryOpen && agents.map(agent => renderAgentButton(agent, false))}
              </>
            ) : (
              // Sidebar collapsed: Show only category icon
              <li className="px-2 mt-5">
                <button
                  onClick={() => handleCategoryToggle(category)}
                  className="w-full flex items-center justify-center p-2 rounded-md transition-colors duration-150 group text-brand-sidebar-text hover:bg-brand-sidebar-hover-bg hover:text-brand-accent"
                  title={category}
                >
                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                    {React.isValidElement(categoryIcon) 
                      ? React.cloneElement(categoryIcon as React.ReactElement<{ className?: string }>, {
                          className: 'w-6 h-6 text-brand-sidebar-text group-hover:text-brand-accent'
                        })
                      : categoryIcon}
                  </div>
                </button>
              </li>
            )}
        </React.Fragment>
      );
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-30 md:hidden"
          style={{ backgroundColor: '#000000' }}
          onClick={toggleSidebar}
          aria-hidden="true"
        ></div>
      )}
      <nav 
        className={`fixed top-0 left-0 h-full bg-muted text-brand-sidebar-text shadow-lg z-40 transition-transform duration-300 ease-in-out transform md:translate-x-0
                    ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64 md:w-20'}`}
        aria-label="Sidebar Principal"
      >
        <div className="flex flex-col h-full">
          <div className={`flex items-center justify-between h-16 px-4 border-b border-[#E0D5C7]/20 bg-muted ${isOpen ? '' : 'md:px-0 md:justify-center'}`}>
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                const welcomeAgent = AGENTS?.find(a => a.id === AgentType.WELCOME);
                if (welcomeAgent) {
                  onSelectAgent(welcomeAgent.id);
                }
              }}
              className={`flex items-center flex-shrink-0 ${isOpen ? 'h-10 max-w-48' : 'h-8 w-8'}`}
              title="Ir para a página inicial EscriturIA"
            >
              <img 
                src={sidebarLogoSrc}
                alt="Escrituria Logo"
                className="w-full h-full object-contain object-center transition-all duration-300 ease-in-out" 
              />
            </a>
            <button onClick={toggleSidebar} className="text-[#5D3A1A] hover:text-[#C4943B] md:hidden" aria-label="Fechar sidebar">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {agentsError && (
              <div className="px-4 py-8 text-center text-red-400 text-sm">
                Erro ao carregar agentes: {agentsError}
              </div>
            )}
            <ul className="py-4 space-y-0" role="menu">
              {!agentsLoading && topLevelAgents.map(agent => renderAgentButton(agent, false))}
              
              {/* Linha separadora entre Devocional Pessoal e Investigando textos */}
              {topLevelAgents.length > 0 && (
                <li className="px-2 my-4">
                  <hr style={{ borderColor: 'rgba(196, 185, 154, 0.3)', borderWidth: '0.5px' }} />
                </li>
              )}
              
              {categoryOrder.map((category) => {
                  const agents = agentsByCategory[category];
                  if (!agents || agents.length === 0) return null;
                  
                  // Adicionar linha separadora abaixo de PREPARING_MESSAGES
                  const shouldAddSeparator = category === AgentCategory.PREPARING_MESSAGES;
                  
                  return (
                    <React.Fragment key={category}>
                      {renderCategory(category, agents)}
                      {shouldAddSeparator && (
                        <li className="px-2 my-4" key={`separator-${category}`}>
                          <hr style={{ borderColor: 'rgba(196, 185, 154, 0.3)', borderWidth: '0.5px' }} />
                        </li>
                      )}
                    </React.Fragment>
                  );
              })}
              
              {/* Linha separadora acima de Minha Conta */}
              <li className="px-2 my-4">
                <hr style={{ borderColor: 'rgba(196, 185, 154, 0.3)', borderWidth: '0.5px' }} />
              </li>
              
              {/* Profile Link - Minha Conta */}
              <li className="px-2 mt-6">
                <button
                  onClick={() => {
                    navigate('/app/profile');
                    setFlyoutCategory(null);
                  }}
                  className={`w-full flex items-center p-2 rounded-md transition-colors duration-150 group bg-brand-bg-dark text-brand-sidebar-text hover:bg-brand-sidebar-hover-bg
                    ${isOpen ? 'justify-start' : 'md:justify-center'}
                  `}
                  title="Minha Conta"
                >
                  {!isOpen && (
                    <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                      <UserCircleIcon className="w-6 h-6 text-brand-sidebar-text" />
                    </div>
                  )}
                  <span className={`${!isOpen ? 'ml-3' : ''} text-sm font-medium whitespace-nowrap ${isOpen ? 'inline' : 'md:hidden'}`}>
                    Minha Conta
                  </span>
                </button>
              </li>
              
              {/* Central de Mensagens */}
              <li className="px-2 mt-1">
                <button
                  onClick={() => {
                    navigate('/app/support-messages');
                    setFlyoutCategory(null);
                  }}
                  className={`w-full flex items-center p-2 rounded-md transition-colors duration-150 group bg-brand-bg-dark text-brand-sidebar-text hover:bg-brand-sidebar-hover-bg
                    ${isOpen ? 'justify-start' : 'md:justify-center'}
                  `}
                  title="Central de Mensagens"
                >
                  {!isOpen && (
                    <div className="flex items-center justify-center w-6 h-6 flex-shrink-0 relative">
                      <ChatBubbleLeftRightIcon className="w-6 h-6 text-brand-sidebar-text" />
                      {unreadNotificationsCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center text-[10px] font-bold bg-red-600 text-white rounded-full">
                          {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                        </span>
                      )}
                    </div>
                  )}
                  <span className={`${!isOpen ? 'ml-3' : ''} text-sm font-medium whitespace-nowrap ${isOpen ? 'inline' : 'md:hidden'} flex items-center gap-2`}>
                    Central de Mensagens
                    {isOpen && unreadNotificationsCount > 0 && (
                      <span className="min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center text-xs font-bold bg-red-600 text-white rounded-full">
                        {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                      </span>
                    )}
                  </span>
                </button>
              </li>
              
              {/* Linha separadora abaixo de Central de Mensagens */}
              <li className="px-2 mt-1 mb-2.5">
                <hr style={{ borderColor: 'rgba(196, 185, 154, 0.3)', borderWidth: '0.5px' }} />
              </li>
              
              {/* Admin Link - Only for superusers */}
              {user?.isSuperuser && getBackendDirectOrigin() && (
                <li className="px-2 mt-1">
                  <a
                    href={`${getBackendDirectOrigin()}/admin/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full flex items-center p-2 rounded-md transition-colors duration-150 group bg-brand-bg-dark text-brand-sidebar-text hover:bg-brand-sidebar-hover-bg
                      ${isOpen ? 'justify-start' : 'md:justify-center'}
                    `}
                    title="Painel Administrativo"
                  >
                    {!isOpen && (
                      <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                        <Cog6ToothIcon className="w-6 h-6 text-brand-sidebar-text" />
                      </div>
                    )}
                    <span className={`${!isOpen ? 'ml-3' : ''} text-sm font-medium whitespace-nowrap ${isOpen ? 'inline' : 'md:hidden'}`}>
                      Painel Admin
                    </span>
                  </a>
                </li>
              )}
              
              {/* Logout Button */}
              <li className="px-2 mt-1">
                <button
                  onClick={logout}
                  className={`w-full flex items-center p-2 rounded-md transition-colors duration-150 group bg-brand-bg-dark text-brand-sidebar-text hover:bg-brand-sidebar-hover-bg
                    ${isOpen ? 'justify-start' : 'md:justify-center'}
                  `}
                  title="Sair"
                >
                  {!isOpen && (
                    <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                      <ArrowLeftStartOnRectangleIcon className="w-6 h-6 text-brand-sidebar-text" />
                    </div>
                  )}
                  <span className={`${!isOpen ? 'ml-3' : ''} text-sm font-medium whitespace-nowrap ${isOpen ? 'inline' : 'md:hidden'}`}>
                    Sair
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Flyout Menu for Categories (when sidebar is collapsed) */}
      {!isOpen && flyoutCategory && (
        <div
          ref={flyoutRef}
          className="fixed left-20 top-16 bottom-0 w-64 border-r border-border shadow-2xl z-40 overflow-y-auto hidden md:block"
          style={{
            backgroundColor: isLightMode ? '#f5e9d8' : '#0f0502',
            height: 'calc(100vh - 4rem)',
          }}
        >
          <div className="p-4">
            <h3 className="text-xs font-semibold tracking-wide uppercase text-brand-text-secondary mb-4 pb-2 border-b border-brand-border-light">
              {flyoutCategory}
            </h3>
            <ul>
              {agentsByCategory[flyoutCategory]?.map(agent => renderAgentButton(agent, true))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
